/* eslint-disable @typescript-eslint/no-empty-object-type */
import Modal from '@/components/_ui/modal/modal';
import { ModalCloseButton } from '@/components/_ui/modal';
import { useCustomToast } from '@/hooks/use-custom-toast';
import { handleDomBubbleEvent } from '@/utils/dom';
import { memo, useCallback, useMemo } from 'react';
import { useSignOut } from '@/features/auth/sign-out/api/sign-out.api';
import { type AxiosErrorCustom, axiosExtractErrorMessage } from '@/lib/axios';
import useAuthContext from '@/features/auth/_shared/contexts/auth.context';
import { useSocket } from '@/websockets/hooks/use-socket';
import Button from '@/components/_ui/button/button';
import { useAuthModalRootActions } from '../auth-modal-root';

export type TSignOutModalType = 'SignOutModal';
export interface ISignOutModalProps {}

const SignOutModal = memo(() => {
  const showToast = useCustomToast();
  const { handleLogout } = useAuthContext();
  const { disconnect } = useSocket();
  const authModalActions = useAuthModalRootActions();

  const mutationSignOut = useSignOut({
    mutationConfig: {
      onError: (error) => {
        const errorMessage = axiosExtractErrorMessage(error as AxiosErrorCustom) || 'Failed to logout!';
        showToast({
          type: 'error',
          message: errorMessage,
        });
      },
      onSuccess: () => {
        handleLogout();
        disconnect();
        authModalActions.closeModal('SignOutModal');
      },
    },
  });

  const isOpen = useMemo(() => {
    return authModalActions.checkIsOpenModal('SignOutModal');
  }, [authModalActions]);

  const handleCloseModal = useCallback(() => {
    authModalActions.closeModal('SignOutModal');
  }, [authModalActions]);

  const handleSignout = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      handleDomBubbleEvent(e);
      mutationSignOut.mutate(undefined);
    },
    [mutationSignOut]
  );

  return (
    <Modal
      isOpen={isOpen}
      isCloseOutside={false}
      isButtonClose={true}
      onClose={handleCloseModal}
      className="w-[500px] max-w-[500px]"
    >
      <div className="pb-[4px] flex flex-col gap-2">
        <h3 className="font-bold text-[18px] text-[#0C8DBF]">Sign Out</h3>
      </div>
      <form id="sign-out-form" className="grid gap-[16px]">
        <span className="text-[14px] font-normal text-[#9D9D9C]">Do you want to sign out?</span>
      </form>

      <div className="flex justify-end gap-[16px] w-full mt-[16px]">
        <ModalCloseButton
          onClick={(e) => {
            handleDomBubbleEvent(e);
            handleCloseModal();
          }}
        >
          Cancel
        </ModalCloseButton>

        <Button
          type="button"
          variant="primary"
          disabled={mutationSignOut.isPending}
          isLoading={mutationSignOut.isPending}
          onClick={(e) => {
            handleSignout(e);
          }}
        >
          Sign Out
        </Button>
      </div>
    </Modal>
  );
});
SignOutModal.displayName = 'SignOutModal';
export default SignOutModal;
